// ────────────────────────────────────────────────────────────────────
// AssetNavPanel — the plant asset worklist (search + class filter +
// service-grouped, risk-sorted list). Extracted from SiteMapScreen so the
// same navigator can host on the asset workspace as a master-detail sidebar.
//
// Pure presentation over a pool of assets; it owns its own filter/group/expand
// state. Clicking a row navigates to that asset. Pass `activeAssetId` to
// highlight the asset currently open (asset-workspace use).
// ────────────────────────────────────────────────────────────────────

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, X, AlertTriangle, ChevronDown } from "lucide-react";
import type { Asset } from "@/lib/types";
import { useWorklist } from "./WorklistContext";

// Per-asset health from worst_cml_status — three channels (colour + text, +
// icon for crit). "" = no reading yet, shown as an explicit em-dash, never 0.
const HEALTH: Record<string, { label: string; color: string }> = {
  crit:  { label: "Critical", color: "var(--err)" },
  warn:  { label: "Warn",     color: "var(--warn)" },
  watch: { label: "Watch",    color: "#CA8A04" },
  ok:    { label: "OK",       color: "var(--ok)" },
  "":    { label: "No data",  color: "#9CA3AF" },
};
const RISK_RANK: Record<string, number> = { crit: 4, warn: 3, watch: 2, ok: 1, "": 0 };

// Best-guess legend for the commodity/service codes embedded in the line tag
// (BYA <size>-<SERVICE>-<lineclass>-<seq>). Proxy only — confirm against the
// operator's spec; unknown codes just show the raw code.
const SERVICE_LABEL: Record<string, string> = {
  PW: "Produced Water", PF: "Produced Fluid", VH: "Vent Header", CD: "Closed Drain",
  OD: "Open Drain", DF: "Diesel Fuel", FG: "Fuel Gas", SW: "Sea Water", JW: "Jacket Water",
  CI: "Chemical Injection", IA: "Instrument Air", UA: "Utility Air", PL: "Pipeline",
  GL: "Gas Lift", HY: "Hydraulic", FW: "Fire Water",
};
const UNCLASSIFIED = "—UNCLASSIFIED";

// Parse a piping line tag into its service + line-class segments. A real line
// reads "<prefix> <size>-<SERVICE>-<lineclass>-<seq>"; anything else (GVI/EXTERNAL
// report rows that leaked in as assets) returns isLine:false → Unclassified bucket.
function parseTag(tag: string): { service: string; lineClass: string; isLine: boolean } {
  const t = (tag || "").split(/[\s-]+/).filter(Boolean);
  // A real line: <prefix> <size> <SERVICE> <lineclass-with-a-digit> … — requiring
  // a digit in the line-class rejects vessel names (Degassing/Launcher) that
  // otherwise pass as a line-class word.
  if (t.length >= 4 && /^[\d.]+$/.test(t[1]) && /^[A-Za-z]{1,4}$/.test(t[2]) && /^[A-Za-z0-9]*\d[A-Za-z0-9]*$/.test(t[3])) {
    return { service: t[2].toUpperCase(), lineClass: t[3].toUpperCase(), isLine: true };
  }
  return { service: "", lineClass: "", isLine: false };
}

// Inspection discipline — asset_class is unreliable here (all "other"), so split
// on the tag: a proper line tag = piping (API 570); vessel/drum/tank names =
// static equipment (API 510); report/external rows = unclassified.
const EQ_WORDS = /drum|vessel|separator|launcher|receiver|scrubber|filter|tank|cooler|exchanger|boot|sump|caisson|coalescer|knock|\bko\b|column|tower|reactor|accumulator|deaerat|pump can/i;
const REPORT_WORDS = /report|inspection|\bgvi\b|external/i;
type Discipline = "piping" | "static" | "tank" | "other";
function disciplineOf(a: Asset): Discipline {
  // Trust an explicit asset_class when the data has one (the demo + any properly
  // classed tenant). Only fall back to tag heuristics for tenants whose class is
  // all "other" (e.g. Valeura's imported line tags).
  switch (a.asset_class) {
    case "piping": return "piping";
    case "tank": return "tank";
    case "vessel":
    case "heat_exchanger":
    case "sphere":
    case "cylo": return "static";
  }
  const t = a.tag || "";
  if (REPORT_WORDS.test(t)) return "other";
  if (parseTag(t).isLine && !EQ_WORDS.test(t)) return "piping";
  if (EQ_WORDS.test(t) || /_[A-Z]+-?\d/.test(t)) return "static";
  return "other";
}

const isHot = (a: Asset): boolean => a.worst_cml_status === "crit" || a.worst_cml_status === "warn";
const critCount = (items: Asset[]): number => items.filter(isHot).length;   // crit + warn (needs-attention)
const critOnly = (items: Asset[]): number => items.filter((a) => a.worst_cml_status === "crit").length;
const warnOnly = (items: Asset[]): number => items.filter((a) => a.worst_cml_status === "warn").length;
// Sort key: crit dominates, then warn — so a group with 3 crit outranks one with 8 warn.
const riskKey = (items: Asset[]): number => critOnly(items) * 100000 + critCount(items);

// Red = critical, amber = warn. A warn group must NOT show a red "crit" badge.
function RiskBadge({ items }: { items: Asset[] }): JSX.Element | null {
  const c = critOnly(items), w = warnOnly(items);
  if (c > 0) return <span className="inline-flex items-center gap-1 font-mono shrink-0 px-1.5 py-px" style={{ fontSize: 9, color: "#fff", background: "var(--err)" }}><AlertTriangle size={9} />{c}</span>;
  if (w > 0) return <span className="inline-flex items-center gap-1 font-mono shrink-0 px-1.5 py-px" style={{ fontSize: 9, color: "#7c5310", background: "rgba(217,119,6,0.18)", border: "1px solid rgba(217,119,6,0.4)" }}>{w}</span>;
  return null;
}

// Active row highlight — provided once at the panel root so AssetRow can read it
// without threading the id through the group components.
const ActiveAssetCtx = createContext<number | undefined>(undefined);

interface Props {
  /** Source pool to list (site map → area assets; asset page → plant assets). */
  assets: Asset[];
  /** Highlight the asset currently open (master-detail on the asset page). */
  activeAssetId?: number;
  /** Plant scope for the shared filter/expand state — carries the inspector's
   *  view across the Site Map and asset screens; resets when it changes. */
  plantId?: number;
  /** Show the "Loading assets…" state while the source query is pending. */
  loading?: boolean;
  /** Message shown when the source pool itself is empty. */
  emptyHint?: string;
  /** Extra classes on the root (defaults to filling the host height). */
  className?: string;
}

export function AssetNavPanel({ assets: allAssets, activeAssetId, plantId, loading, emptyHint, className }: Props): JSX.Element {
  // Filter/group/expand state is shared (scoped to the plant) so it follows the
  // inspector across the Site Map ↔ asset screens. See WorklistContext.
  const { search, setSearch, cls, setCls, sortBy, setSortBy, grouped, setGrouped, expanded, setExpanded, topOpen, setTopOpen } = useWorklist(plantId);
  // Local "needs attention" filter — show only crit/warn assets (resets per visit).
  const [attnOnly, setAttnOnly] = useState(false);
  const attnCount = useMemo(() => allAssets.filter(isHot).length, [allAssets]);

  // Class chips with counts (only classes present)
  const classCounts = useMemo(() => {
    const m: Record<string, number> = { ALL: allAssets.length };
    for (const a of allAssets) m[a.asset_class] = (m[a.asset_class] ?? 0) + 1;
    return m;
  }, [allAssets]);

  const assets = useMemo(() => {
    let pool = cls === "ALL" ? allAssets : allAssets.filter((a) => a.asset_class === cls);
    const q = search.trim().toLowerCase();
    if (q) pool = pool.filter((a) =>
      a.tag.toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q) || (a.service || "").toLowerCase().includes(q));
    if (attnOnly) pool = pool.filter(isHot);
    return [...pool].sort((a, b) =>
      sortBy === "tag"
        ? a.tag.localeCompare(b.tag, undefined, { numeric: true })
        : (RISK_RANK[b.worst_cml_status] - RISK_RANK[a.worst_cml_status]) || b.cml_count - a.cml_count || a.tag.localeCompare(b.tag));
  }, [allAssets, cls, search, sortBy, attnOnly]);

  // Split by inspection discipline first (static equipment vs process piping).
  const piping = useMemo(() => assets.filter((a) => disciplineOf(a) === "piping"), [assets]);
  const staticEq = useMemo(() => assets.filter((a) => disciplineOf(a) === "static"), [assets]);
  const tanks = useMemo(() => assets.filter((a) => disciplineOf(a) === "tank"), [assets]);
  const other = useMemo(() => assets.filter((a) => disciplineOf(a) === "other"), [assets]);
  const toggleTop = (d: Discipline): void => setTopOpen((p) => { const n = new Set(p); n.has(d) ? n.delete(d) : n.add(d); return n; });

  // Group the piping assets by service code parsed from the tag. Within a group,
  // assets sort by line-class then risk; groups order worst-first.
  const groups = useMemo(() => {
    const m = new Map<string, Asset[]>();
    for (const a of piping) {
      const p = parseTag(a.tag);
      const key = p.isLine ? p.service : UNCLASSIFIED;
      (m.get(key) ?? m.set(key, []).get(key)!).push(a);
    }
    for (const items of m.values()) {
      items.sort((x, y) =>
        parseTag(x.tag).lineClass.localeCompare(parseTag(y.tag).lineClass)
        || RISK_RANK[y.worst_cml_status] - RISK_RANK[x.worst_cml_status]
        || x.tag.localeCompare(y.tag));
    }
    return [...m.entries()].sort((A, B) =>
      A[0] === UNCLASSIFIED ? 1 : B[0] === UNCLASSIFIED ? -1
        : riskKey(B[1]) - riskKey(A[1]) || B[1].length - A[1].length || A[0].localeCompare(B[0]));
  }, [piping]);

  const isOpen = (svc: string, items: Asset[]): boolean =>
    expanded ? expanded.has(svc) : critCount(items) > 0;
  function toggleGroup(svc: string): void {
    setExpanded((prev) => {
      const base = prev ?? new Set(groups.filter(([, it]) => critCount(it) > 0).map(([s]) => s));
      const next = new Set(base);
      next.has(svc) ? next.delete(svc) : next.add(svc);
      return next;
    });
  }

  return (
    <div className={"flex flex-col min-h-0 " + (className ?? "h-full")}>
      {/* filter bar */}
      <div className="px-4 py-3 border-b flex flex-col gap-2" style={{ borderColor: "var(--hairline)" }}>
        <div className="flex items-center gap-2 px-2.5 py-1.5" style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
          <Search size={13} style={{ color: "var(--ink-4)" }} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setExpanded(null); }} placeholder="Search assets — tag, service…" className="flex-1 bg-transparent outline-none font-mono" style={{ fontSize: 12, color: "var(--ink-1)" }} />
          {search && <button onClick={() => { setSearch(""); setExpanded(null); }} style={{ color: "var(--ink-3)" }}><X size={13} /></button>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {Object.keys(classCounts).filter((k) => k === "ALL" || classCounts[k]).map((k) => (
            <button key={k} onClick={() => { setCls(k); setExpanded(null); }} className="font-mono uppercase px-2 py-1 transition-colors"
              style={{ fontSize: 9, letterSpacing: ".1em", border: "1px solid " + (cls === k ? "var(--accent)" : "var(--hairline)"),
                       background: cls === k ? "rgba(var(--accent-rgb) / 0.08)" : "transparent", color: cls === k ? "var(--accent-deep)" : "var(--ink-3)" }}>
              {k === "ALL" ? "ALL" : k.replace("_", " ")} {classCounts[k]}
            </button>
          ))}
          {attnCount > 0 && (
            <button onClick={() => { setAttnOnly((v) => !v); setExpanded(null); }}
              title="Show only assets that need attention (critical / warn)"
              className="font-mono uppercase px-2 py-1 inline-flex items-center gap-1 transition-colors"
              style={{ fontSize: 9, letterSpacing: ".1em",
                       border: "1px solid " + (attnOnly ? "var(--err)" : "var(--hairline)"),
                       background: attnOnly ? "rgba(192,57,43,0.08)" : "transparent",
                       color: attnOnly ? "var(--err)" : "var(--ink-3)" }}>
              <AlertTriangle size={9} /> ATTN {attnCount}
            </button>
          )}
          <button onClick={() => setGrouped((g) => !g)} title="Group assets by service (corrosion circuit) or show a flat list"
            className="ml-auto font-mono uppercase px-2 py-1 transition-colors" style={{ fontSize: 9, letterSpacing: ".1em",
              border: "1px solid " + (grouped ? "var(--accent)" : "var(--hairline)"),
              background: grouped ? "rgba(var(--accent-rgb) / 0.08)" : "transparent", color: grouped ? "var(--accent-deep)" : "var(--ink-3)" }}>
            {grouped ? "⊞ SERVICE" : "≣ FLAT"}
          </button>
          {!grouped && (
            <button onClick={() => setSortBy((s) => (s === "risk" ? "tag" : "risk"))} title="Toggle sort"
              className="font-mono uppercase px-2 py-1 transition-colors" style={{ fontSize: 9, letterSpacing: ".1em", border: "1px solid var(--hairline)", color: sortBy === "risk" ? "var(--accent-deep)" : "var(--ink-3)" }}>
              {sortBy === "risk" ? "RISK ↓" : "TAG A–Z"}
            </button>
          )}
        </div>
      </div>

      {/* compact rows */}
      <div className="flex-1 overflow-auto">
        {loading && <div className="px-4 py-4 font-mono text-xs" style={{ color: "var(--ink-3)" }}>Loading assets…</div>}
        {!loading && assets.length === 0 && (
          <div className="px-4 py-8 text-center font-mono text-xs" style={{ color: "var(--ink-3)" }}>
            {search || cls !== "ALL" ? "No assets match the filter." : (emptyHint ?? "No assets registered.")}
          </div>
        )}
        <ActiveAssetCtx.Provider value={activeAssetId}>
          {grouped
            ? (
              <>
                {piping.length > 0 && (
                  <TopSection title="Process Piping" subtitle="API 570" assets={piping} open={topOpen.has("piping")} onToggle={() => toggleTop("piping")}>
                    {/* When no real service codes parse (clean tags), list the
                        lines directly instead of a redundant "Unclassified" group. */}
                    {groups.length === 1 && groups[0][0] === UNCLASSIFIED
                      ? groups[0][1].map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)
                      : groups.map(([svc, items]) => (
                          <ServiceGroup key={svc} svc={svc} items={items} open={isOpen(svc, items)} onToggle={() => toggleGroup(svc)} />
                        ))}
                  </TopSection>
                )}
                {staticEq.length > 0 && (
                  <TopSection title="Static Equipment" subtitle="API 510" assets={staticEq} open={topOpen.has("static")} onToggle={() => toggleTop("static")}>
                    {staticEq.map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)}
                  </TopSection>
                )}
                {tanks.length > 0 && (
                  <TopSection title="Tanks" subtitle="API 653" assets={tanks} open={topOpen.has("tank")} onToggle={() => toggleTop("tank")}>
                    {tanks.map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)}
                  </TopSection>
                )}
                {other.length > 0 && (
                  <TopSection title="Unclassified" subtitle="reports / repairs" assets={other} open={topOpen.has("other")} onToggle={() => toggleTop("other")}>
                    {other.map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)}
                  </TopSection>
                )}
              </>
            )
            : assets.map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)}
        </ActiveAssetCtx.Provider>
      </div>
    </div>
  );
}

// ── Top-level discipline section (Process Piping / Static Equipment / …) ──
function TopSection({ title, subtitle, assets, open, onToggle, children }: {
  title: string; subtitle: string; assets: Asset[]; open: boolean; onToggle: () => void; children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ borderBottom: "2px solid var(--hairline)" }}>
      {/* Header sticks to the top of the scroll area so you always know which
          discipline you're in on a long list; a slim health bar shows its mix. */}
      <div style={{ position: "sticky", top: 0, zIndex: 2, background: "var(--paper)" }}>
        <button onClick={onToggle} className="w-full px-3 py-3 flex items-center gap-2 text-left transition-colors hover:brightness-95">
          <ChevronDown size={14} className="shrink-0 transition-transform" style={{ color: "var(--ink-3)", transform: open ? "" : "rotate(-90deg)" }} />
          <span className="font-mono uppercase" style={{ fontSize: 12, letterSpacing: ".1em", color: "var(--ink-1)", fontWeight: 700 }}>{title}</span>
          <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".12em", color: "var(--ink-4)" }}>{subtitle}</span>
          <span className="ml-auto font-mono tabular-nums shrink-0" style={{ fontSize: 10, color: "var(--ink-3)" }}>{assets.length}</span>
          <RiskBadge items={assets} />
        </button>
        <HealthBar assets={assets} />
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

// Slim proportional health bar: critical / warn / ok+watch / no-data.
function HealthBar({ assets }: { assets: Asset[] }): JSX.Element | null {
  const total = assets.length;
  if (!total) return null;
  const c = critOnly(assets), w = warnOnly(assets);
  const ok = assets.filter((a) => a.worst_cml_status === "ok" || a.worst_cml_status === "watch").length;
  const nd = total - c - w - ok;
  const seg = (n: number, color: string) =>
    n > 0 ? <div style={{ width: `${(n / total) * 100}%`, background: color }} /> : null;
  return (
    <div className="flex w-full" style={{ height: 3 }}
         title={`${c} critical · ${w} warn · ${ok} ok · ${nd} no data`}>
      {seg(c, "var(--err)")}{seg(w, "var(--warn)")}{seg(ok, "var(--ok)")}{seg(nd, "#D1D5DB")}
    </div>
  );
}

// ── Service group (corrosion circuit) — collapsible, line-class sub-headers ──
function ServiceGroup({ svc, items, open, onToggle }: {
  svc: string; items: Asset[]; open: boolean; onToggle: () => void;
}): JSX.Element {
  const isUn = svc === UNCLASSIFIED;
  const label = isUn ? "non-line tags" : SERVICE_LABEL[svc] || "";
  const byClass = useMemo(() => {
    const m = new Map<string, Asset[]>();
    for (const a of items) { const lc = parseTag(a.tag).lineClass || "—"; (m.get(lc) ?? m.set(lc, []).get(lc)!).push(a); }
    return [...m.entries()].sort((A, B) => riskKey(B[1]) - riskKey(A[1]) || A[0].localeCompare(B[0]));
  }, [items]);
  // line-class collapse — null = default (crit classes open), Set once toggled.
  // Reset to default whenever this group's membership changes (e.g. a filter),
  // so a previously-collapsed class doesn't stay hidden against a new result set.
  const [lcExpanded, setLcExpanded] = useState<Set<string> | null>(null);
  const memberSig = items.map((a) => a.id).join(",");
  useEffect(() => { setLcExpanded(null); }, [memberSig]);
  const lcOpen = (lc: string, las: Asset[]): boolean => lcExpanded ? lcExpanded.has(lc) : critCount(las) > 0;
  function toggleLc(lc: string): void {
    setLcExpanded((prev) => {
      const base = prev ?? new Set(byClass.filter(([, it]) => critCount(it) > 0).map(([l]) => l));
      const next = new Set(base);
      next.has(lc) ? next.delete(lc) : next.add(lc);
      return next;
    });
  }
  return (
    <div className="border-b" style={{ borderColor: "var(--hairline)" }}>
      <button onClick={onToggle} className="w-full px-3 py-2.5 flex items-center gap-2 text-left transition-colors hover:brightness-95" style={{ background: "var(--paper-2)" }}>
        <ChevronDown size={13} className="shrink-0 transition-transform" style={{ color: "var(--ink-4)", transform: open ? "" : "rotate(-90deg)" }} />
        <span className="font-mono uppercase" style={{ fontSize: 12, letterSpacing: ".06em", color: isUn ? "var(--ink-3)" : "var(--accent-deep)", fontWeight: 700 }}>{isUn ? "UNCLASSIFIED" : svc}</span>
        {label && <span className="font-sans truncate" style={{ fontSize: 11, color: "var(--ink-3)" }}>{label}</span>}
        <span className="ml-auto font-mono tabular-nums shrink-0" style={{ fontSize: 10, color: "var(--ink-3)" }}>{items.length}</span>
        <RiskBadge items={items} />
      </button>
      {open && byClass.map(([lc, las]) => (
        <div key={lc}>
          {!isUn && (
            <button onClick={() => toggleLc(lc)} className="w-full pl-6 pr-3 py-1 flex items-center gap-2 font-mono uppercase text-left transition-colors hover:bg-[rgba(10,22,40,0.04)]"
              style={{ fontSize: 9, letterSpacing: ".12em", color: "var(--ink-4)", background: "rgba(10,22,40,0.02)" }}>
              <ChevronDown size={10} className="shrink-0 transition-transform" style={{ color: "var(--ink-4)", transform: lcOpen(lc, las) ? "" : "rotate(-90deg)" }} />
              <span style={{ color: "var(--ink-3)", fontWeight: 700 }}>{lc}</span><span className="opacity-40">·</span><span>{las.length}</span>
              {critOnly(las) > 0 && <span className="ml-auto" style={{ color: "var(--err)" }}>{critOnly(las)} crit</span>}
              {critOnly(las) === 0 && warnOnly(las) > 0 && <span className="ml-auto" style={{ color: "#b06d12" }}>{warnOnly(las)} warn</span>}
            </button>
          )}
          {(isUn || lcOpen(lc, las)) && las.map((a, i) => <AssetRow key={a.id} a={a} idx={i} />)}
        </div>
      ))}
    </div>
  );
}

// ── Asset worklist row (compact, fits the sidebar) ──────────────────
function AssetRow({ a, idx }: { a: Asset; idx: number }): JSX.Element {
  const navigate = useNavigate();
  const activeId = useContext(ActiveAssetCtx);
  const active = activeId != null && a.id === activeId;
  const h = HEALTH[a.worst_cml_status] ?? HEALTH[""];
  const hot = a.worst_cml_status === "crit" || a.worst_cml_status === "warn";
  // When this row becomes the open asset, pull it into view (long lists).
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (active) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [active]);
  return (
    <motion.button
      ref={rowRef}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2, delay: Math.min(idx, 12) * 0.015 }}
      onClick={() => navigate(`/assets/${a.id}`)}
      className="w-full px-4 py-2 text-left border-b hover:bg-[rgba(var(--accent-rgb)_/_0.05)] transition-colors group block"
      style={{ borderColor: "var(--hairline)",
               borderLeft: `${hot ? 3 : 2}px solid ${a.worst_cml_status ? h.color : "transparent"}`,
               // Faint exception tint so a crit/warn row stands out in a long sea of OK.
               background: active ? "rgba(var(--accent-rgb) / 0.12)"
                 : a.worst_cml_status === "crit" ? "rgba(192,57,43,0.055)"
                 : a.worst_cml_status === "warn" ? "rgba(217,119,6,0.06)" : undefined }}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono truncate flex-1" style={{ fontSize: 12, color: "var(--accent-deep)", fontWeight: 600 }}>{a.tag}</span>
        {a.worst_cml_status ? (
          <span className="inline-flex items-center gap-1 font-mono uppercase shrink-0" style={{ fontSize: 9, letterSpacing: ".06em", color: h.color }}>
            {hot && <AlertTriangle size={10} />}{h.label}
          </span>
        ) : <span className="font-mono shrink-0" style={{ fontSize: 9, color: "var(--ink-4)" }}>—</span>}
        <ArrowUpRight size={12} className="shrink-0 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" style={{ color: "var(--ink-4)" }} />
      </div>
      <div className="flex items-center gap-1.5 mt-0.5 font-mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
        {/* asset_class is "other" for every imported line — drop it as noise; keep it
            only when it actually classifies (piping/vessel/…). */}
        {a.asset_class && a.asset_class !== "other" && (
          <span className="uppercase tracking-wider">{a.asset_class.replace("_", " ")}</span>
        )}
        {a.service && <span className="truncate" style={{ color: "var(--ink-3)" }}>{a.service}</span>}
        <span className="ml-auto shrink-0 tabular-nums">{a.cml_count} CML</span>
        <span className="opacity-40">·</span>
        <span className="shrink-0 tabular-nums" title="Last inspection date">
          {a.latest_inspection_date ? a.latest_inspection_date.slice(0, 10) : "no insp"}
        </span>
      </div>
    </motion.button>
  );
}
